/**
 * @fileoverview Chat-only practice-reference side panel renderer.
 *
 * Builds the in-chat right-side panel DOM from a `<practice-reference>` card's
 * decoded snapshot. Distinct from `PracticesSection`'s live-state panel by
 * design (per the practice-reference card plan): static, snapshot-driven,
 * no expand/collapse, no action buttons.
 *
 * Visual structure mirrors `PracticesSection.renderDetail` (mock-2):
 *   - header: title + meta pills (category, started date, session)
 *   - action text
 *   - the test block: the prediction, with `whatToNotice` fused in under a
 *     "What To Watch For" sub-label (omitted when the snapshot carries neither)
 *   - "Why This Matters" callout (if rationale present)
 *   - parent-chain section (titles only, non-clickable)
 *   - check-in feed (session-sourced logs, newest first)
 *
 * Safety: all text fields are inserted via `textContent`, not `innerHTML`.
 * Only structural elements (divs, spans, sections) are constructed via
 * `createElement`. No markdown rendering.
 *
 * @module chat/practiceReferencePanel
 */

import { buildPredictionBlock } from '../components/predictionBlock.js';
import { buildTakeawayBox } from '../components/takeawayBox.js';

const CHECK_SVG ='<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>';
const ARROW_UP_RIGHT_SVG = '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 8L8 2M4 2h4v4"/></svg>';

function el(tag, className) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

function textEl(tag, className, text) {
  const node = el(tag, className);
  if (text != null && text !== '') node.textContent = String(text);
  return node;
}

function formatStartedDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return '';
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return 'last week';
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 60) return 'last month';
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

function formatFeedDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return sameYear
    ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function buildHeader(snapshot) {
  const header = el('div', 'practice-detail__header');
  const h1 = textEl('h1', 'practice-detail__title', snapshot.title || 'Experiment');
  header.appendChild(h1);

  const meta = el('div', 'practice-detail__meta sd-metadata');

  const cat = snapshot.definition?.category;
  if (cat) {
    meta.appendChild(textEl('span', 'sd-metadata__text practice-detail__meta-tag--category', cat));
  }

  const startedDate = formatStartedDate(snapshot.activatedAt || snapshot.createdAt);
  if (startedDate) {
    meta.appendChild(textEl('span', 'sd-metadata__text', `Started ${startedDate}`));
  }

  if (snapshot.session?.sessionNumber) {
    const sessionPill = el('span', 'tag tag--meta-link');
    sessionPill.textContent = `Session ${snapshot.session.sessionNumber} `;
    const arrow = el('span', 'practice-detail__session-arrow');
    arrow.setAttribute('aria-hidden', 'true');
    arrow.innerHTML = ARROW_UP_RIGHT_SVG;
    sessionPill.appendChild(arrow);
    meta.appendChild(sessionPill);
  }

  header.appendChild(meta);
  return header;
}

function buildParentChain(parentChain) {
  if (!Array.isArray(parentChain) || parentChain.length === 0) return null;
  const section = el('div', 'practice-detail__lineage');
  section.appendChild(textEl('div', 'practice-detail__lineage-label', 'Earlier in this arc'));
  const list = el('ul', 'practice-detail__lineage-list');
  for (const ancestor of parentChain) {
    const li = el('li', 'practice-detail__lineage-item');
    li.appendChild(textEl('span', 'practice-detail__lineage-title', ancestor.title || 'Earlier experiment'));
    if (ancestor.lastCheckinSummary) {
      li.appendChild(textEl('span', 'practice-detail__lineage-summary', ancestor.lastCheckinSummary));
    }
    list.appendChild(li);
  }
  section.appendChild(list);
  return section;
}

function buildFeedRow(log) {
  // Same `.timeline-row` shape the /practices detail Progress zone uses, so a
  // report reads identically wherever it surfaces.
  const row = el('div', 'timeline-row practice-feed__row practice-feed__row--session');

  const dateCol = el('div', 'timeline-row__date-col');
  dateCol.appendChild(textEl('span', 'timeline-row__date', formatFeedDate(log.loggedAt)));
  row.appendChild(dateCol);

  const content = el('div', 'timeline-row__content');
  const title = textEl('p', 'timeline-row__title', log.title || 'Reported back');
  const check = el('span', 'practice-feed__check');
  check.setAttribute('aria-hidden', 'true');
  check.innerHTML = CHECK_SVG;
  title.prepend(check);
  content.appendChild(title);
  if (log.sessionSummary) {
    content.appendChild(textEl('p', 'timeline-row__detail', log.sessionSummary));
  }
  row.appendChild(content);

  return row;
}

function buildFeed(logs) {
  const feedWrap = el('div', 'practice-detail__feed-wrap');
  if (!Array.isArray(logs) || logs.length === 0) {
    const empty = el('div', 'practice-feed practice-feed--empty');
    empty.textContent = 'Your reports will appear here';
    feedWrap.appendChild(empty);
    return feedWrap;
  }
  const sorted = [...logs].sort(
    (a, b) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime()
  );

  // Activity zone header — same label, classes and count chip as
  // PracticesSection.renderFeed. Deliberately NOT the collapsible form that
  // page uses: this is a compact read-only snapshot in the chat sidebar, with
  // nothing below the feed to collapse it out of the way of, and no
  // wireDisclosures host. Static and clickable zone headers are
  // interchangeable by design (they share their metrics), so the two still
  // read as the same zone.
  const header = el('div', 'sd-section-header practice-detail__progress-header');
  header.textContent = 'Activity';
  const headerCount = el('span', 'sd-zone-count');
  headerCount.textContent = String(sorted.length);
  header.appendChild(headerCount);
  feedWrap.appendChild(header);

  const feed = el('div', 'practice-feed');
  for (const log of sorted) {
    feed.appendChild(buildFeedRow(log));
  }
  feedWrap.appendChild(feed);
  return feedWrap;
}

/**
 * Build the in-chat right-side panel DOM for a practice-reference snapshot.
 *
 * @param {Object} snapshot - Decoded JSON from the `<practice-reference>` `<detail>` element
 * @returns {HTMLElement} container element ready to pass to RightPanelManager
 */
export function renderPracticeReferencePanel(snapshot) {
  const container = el('div', 'practice-detail practice-detail--reference');

  container.appendChild(buildHeader(snapshot));

  const body = el('div', 'practice-detail__body practice-detail__standalone');

  if (snapshot.action) {
    body.appendChild(textEl('div', 'sd-section-header', 'Description'));
    body.appendChild(textEl('p', 'practice-detail__action-text', snapshot.action));
  }
  // The test — the claim under test plus how you would know. Both halves are
  // soft slots (orientation practices and pre-cutover snapshots carry neither),
  // and with neither the block is omitted rather than rendered empty.
  const testBlock = buildPredictionBlock(snapshot.prediction, { notice: snapshot.whatToNotice });
  if (testBlock) body.appendChild(testBlock);

  if (snapshot.rationale) {
    body.appendChild(buildTakeawayBox({ label: 'Why This Matters', text: snapshot.rationale }));
  }

  const lineage = buildParentChain(snapshot.parentChain);
  if (lineage) body.appendChild(lineage);

  body.appendChild(buildFeed(snapshot.logs));

  container.appendChild(body);
  return container;
}
